import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Droplets, Minus, Plus } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

// Glasses of water (250 ml) for one member on one day — open to every tier
export function WaterTracker({ date, memberId, glasses = 0, goal = 8 }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const change = async (delta) => {
    try {
      const { data } = await api.post("/tracking/water", { date, member_id: memberId, delta });
      qc.setQueryData(["tracking", date], (old) => ({ ...(old || {}), water: data.water }));
      qc.invalidateQueries({ queryKey: ["tracking-history"] });
      if (delta > 0 && data.water[memberId] === goal) toast.success(t("water_done"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const dots = Math.max(goal, glasses);
  return (
    <div className="mt-5 rounded-2xl bg-secondary/60 p-3.5 flex items-center gap-3" data-testid="water-tracker">
      <span className="grid place-items-center h-9 w-9 rounded-full bg-chenia-rosesoft text-chenia-terracottadark shrink-0"><Droplets className="h-4 w-4" /></span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-sm font-medium">{t("water")}</p>
          <p className="text-xs text-muted-foreground" data-testid="water-count">
            <span className="font-semibold text-foreground">{glasses}</span> / {goal} {t("glasses")} · {glasses * 250} ml
          </p>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1" aria-hidden>
          {Array.from({ length: dots }).map((_, i) => (
            <span key={i} className={`h-2 w-4 rounded-full transition-colors duration-300 ${i < glasses ? (i >= goal ? "bg-chenia-sage" : "bg-primary") : "bg-background"}`} />
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => change(-1)} disabled={glasses <= 0} aria-label={t("remove_glass")} data-testid="water-minus"
          className="grid place-items-center h-8 w-8 rounded-full border border-border bg-card hover:bg-background disabled:opacity-40 transition-colors"><Minus className="h-3.5 w-3.5" /></button>
        <button onClick={() => change(1)} aria-label={t("add_glass")} data-testid="water-plus"
          className="grid place-items-center h-8 w-8 rounded-full bg-primary text-primary-foreground hover:bg-chenia-terracottadark transition-colors"><Plus className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}
